import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Clock, Loader2, CheckCircle, AlertTriangle, Zap, TrendingUp, BarChart3, PieChart, Coins } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = (s) => {
  if (s === "completed") return "bg-[#4B6F44] text-white border-[#4B6F44]";
  if (s === "in_progress") return "bg-[#D6AD60]/20 text-[#5C4D24] border-[#D6AD60]";
  return "bg-transparent text-[#9F7D23] border-[#9F7D23]";
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/stats`),
      axios.get(`${API}/orders`),
      axios.get(`${API}/analytics`),
    ]).then(([s, o, a]) => {
      setStats(s.data);
      setOrders(o.data.slice(0, 6));
      setAnalytics(a.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" />
      </div>
    );
  }

  const cards = stats ? [
    { label: "Total Orders", value: stats.total_orders, icon: Clock, color: "text-[#5C4D24]" },
    { label: "Pending", value: stats.pending, icon: AlertTriangle, color: "text-[#D6AD60]" },
    { label: "In Progress", value: stats.in_progress, icon: Loader2, color: "text-[#9F7D23]" },
    { label: "Completed", value: stats.completed, icon: CheckCircle, color: "text-[#4B6F44]" },
    { label: "Revenue (AED)", value: `${stats.total_revenue?.toFixed(0) || 0}`, icon: Coins, color: "text-[#9F7D23]" },
    { label: "Urgent", value: stats.urgent, icon: Zap, color: "text-[#9F3A3A]" },
  ] : [];

  const maxRevenue = analytics?.revenue_by_month?.length > 0 
    ? Math.max(...analytics.revenue_by_month.map(d => d.revenue)) 
    : 1;

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h2 className="font-['Space_Mono'] text-3xl md:text-4xl tracking-tight uppercase text-[#5C4D24]">
            Dashboard
          </h2>
          <p className="text-sm text-[#8B7536] mt-1">Sales & Workshop Overview</p>
        </div>
        <Link
          to="/orders/new"
          data-testid="new-order-btn"
          className="bg-[#9F7D23] text-white hover:bg-[#8A6D1F] px-6 md:px-8 py-3 font-['Space_Mono'] tracking-widest uppercase text-xs transition-all hover:translate-y-[-1px] active:scale-95 flex items-center gap-2"
        >
          New Order <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c, i) => (
          <div
            key={c.label}
            className={`bg-white border border-[#D6D6CF] p-5 hover:border-[#9F7D23] transition-all duration-300 group animate-fade-in-up stagger-${i + 1}`}
            data-testid={`stat-${c.label.toLowerCase().replace(' ', '-')}`}
          >
            <c.icon className={`w-5 h-5 ${c.color} mb-3 group-hover:translate-x-1 transition-transform`} />
            <p className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">{c.label}</p>
            <p className="font-['Space_Mono'] text-2xl text-[#5C4D24] mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Analytics Section */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in-up" data-testid="analytics-section">
          {/* Revenue Chart */}
          <div className="bg-white border border-[#D6D6CF] p-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-[#9F7D23]" />
              <h3 className="font-['Space_Mono'] text-sm tracking-tight uppercase text-[#5C4D24]">Revenue Trend</h3>
            </div>
            {analytics.revenue_by_month?.length > 0 ? (
              <div className="flex items-end gap-3 h-40">
                {analytics.revenue_by_month.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full bg-[#F5F5F0] relative" style={{ height: '120px' }}>
                      <div 
                        className="absolute bottom-0 w-full bg-[#D6AD60] transition-all duration-500"
                        style={{ height: `${(d.revenue / maxRevenue) * 100}%` }}
                      />
                      <div 
                        className="absolute bottom-0 w-full bg-[#9F7D23] transition-all duration-500"
                        style={{ height: `${(d.collected / maxRevenue) * 100}%` }}
                      />
                    </div>
                    <div className="text-center">
                      <p className="font-['Space_Mono'] text-[10px] text-[#8B7536]">{d.month?.slice(5) || 'N/A'}</p>
                      <p className="font-['Space_Mono'] text-xs text-[#5C4D24] font-bold">{d.revenue?.toFixed(0)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-[#8B7536] text-sm">No data yet</div>
            )}
            <div className="flex items-center gap-4 mt-4 text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#D6AD60]"></span> Total Revenue</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#9F7D23]"></span> Collected</span>
            </div>
          </div>

          {/* Top Items */}
          <div className="bg-white border border-[#D6D6CF] p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-[#9F7D23]" />
              <h3 className="font-['Space_Mono'] text-sm tracking-tight uppercase text-[#5C4D24]">Top Items</h3>
            </div>
            {analytics.top_garment_types?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_garment_types.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-[#5C4D24]">{item.name}</span>
                    <div className="text-right">
                      <span className="font-['Space_Mono'] text-xs text-[#9F7D23]">{item.count} sold</span>
                      <p className="font-['Space_Mono'] text-[10px] text-[#8B7536]">{item.revenue?.toFixed(0)} AED</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-[#8B7536] text-sm">No data yet</div>
            )}
          </div>

          {/* Top Collections */}
          <div className="bg-white border border-[#D6D6CF] p-6">
            <div className="flex items-center gap-2 mb-4">
              <PieChart className="w-5 h-5 text-[#9F7D23]" />
              <h3 className="font-['Space_Mono'] text-sm tracking-tight uppercase text-[#5C4D24]">Popular Collections</h3>
            </div>
            {analytics.top_collections?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_collections.map((col, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-[#5C4D24]">{col.name}</span>
                    <span className="font-['Space_Mono'] text-xs text-[#9F7D23]">{col.count} orders</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-[#8B7536] text-sm">No data yet</div>
            )}
          </div>

          {/* Average Order Value */}
          <div className="bg-white border border-[#D6D6CF] p-6 lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <Coins className="w-5 h-5 text-[#9F7D23]" />
              <h3 className="font-['Space_Mono'] text-sm tracking-tight uppercase text-[#5C4D24]">Key Metrics</h3>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-[#F5F5F0]">
                <p className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Avg Order</p>
                <p className="font-['Space_Mono'] text-2xl text-[#5C4D24] mt-1">{analytics.average_order_value?.toFixed(0) || 0}</p>
                <p className="font-['Space_Mono'] text-[10px] text-[#8B7536]">AED</p>
              </div>
              <div className="text-center p-4 bg-[#F5F5F0]">
                <p className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Outstanding</p>
                <p className="font-['Space_Mono'] text-2xl text-[#9F3A3A] mt-1">{stats?.outstanding?.toFixed(0) || 0}</p>
                <p className="font-['Space_Mono'] text-[10px] text-[#8B7536]">AED</p>
              </div>
              <div className="text-center p-4 bg-[#F5F5F0]">
                <p className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Collection Rate</p>
                <p className="font-['Space_Mono'] text-2xl text-[#4B6F44] mt-1">
                  {stats?.total_revenue > 0 ? ((stats.collected / stats.total_revenue) * 100).toFixed(0) : 0}%
                </p>
                <p className="font-['Space_Mono'] text-[10px] text-[#8B7536]">of revenue</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#D6D6CF] p-6 animate-fade-in-up" data-testid="recent-orders">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24]">Recent Orders</h3>
          <Link
            to="/orders"
            data-testid="view-all-orders-link"
            className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#9F7D23] hover:translate-x-1 transition-transform flex items-center gap-1"
          >
            View All <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {orders.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-[#8B7536] text-sm">No orders yet.</p>
            <Link to="/orders/new" className="font-['Space_Mono'] text-xs text-[#9F7D23] uppercase tracking-widest mt-2 inline-block">
              Create your first order
            </Link>
          </div>
        ) : (
          <div>
            {orders.map((order) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                data-testid={`order-row-${order.order_number}`}
                className="flex items-center justify-between py-4 border-b border-[#D6D6CF] last:border-0 hover:translate-x-1 transition-transform group"
              >
                <div className="flex items-center gap-4">
                  <span className="font-['Space_Mono'] text-sm text-[#9F7D23]">{order.order_number}</span>
                  <div className="flex flex-col">
                    <span className="text-[#5C4D24] text-sm">{order.customer?.name}</span>
                    {order.customer?.profile_name && (
                      <span className="text-xs text-[#9F7D23]">For: {order.customer.profile_name}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {order.financial?.is_urgent && (
                    <Zap className="w-4 h-4 text-[#9F3A3A]" />
                  )}
                  {order.financial?.balance_due <= 0 ? (
                    <span className="font-['Space_Mono'] text-[10px] px-2 py-0.5 bg-[#4B6F44] text-white uppercase tracking-widest">Paid</span>
                  ) : (
                    <span className="font-['Space_Mono'] text-[10px] text-[#9F3A3A]">{order.financial?.balance_due} AED due</span>
                  )}
                  <Badge className={`rounded-none font-['Space_Mono'] text-[10px] uppercase tracking-widest px-2 py-0.5 ${statusColor(order.status)}`}>
                    {order.status?.replace("_", " ")}
                  </Badge>
                  <ArrowRight className="w-4 h-4 text-[#8B7536] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
